import React from "react";

const CheckIcon = ({ highlighted = false }) => (
  <svg
    className={`h-5 w-5 flex-shrink-0 ${
      highlighted ? "text-purple-600" : "text-green-500"
    }`}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    aria-hidden="true"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2.5}
      d="M5 13l4 4L19 7"
    />
  </svg>
);

const Pricing = () => {
  const plans = [
    {
      name: "Free",
      description: "Perfect for getting started with your online presence.",
      price: "0",
      buttonText: "Get Started Free",
      href: "/register",
      features: [
        "1 ZeedBio page",
        "Up to 5 links",
        "Social media links",
        "Basic themes",
        "ZeedBio branding",
      ],
      popular: false,
    },
    {
      name: "Pro",
      description:
        "For creators and professionals who want more customization.",
      price: "500",
      buttonText: "Upgrade to Pro",
      href: "/register",
      features: [
        "Everything in Free",
        "Unlimited links",
        "Custom themes",
        "Remove ZeedBio branding",
        "Profile customization",
        "Basic analytics",
        "QR Code",
      ],
      popular: true,
    },
    {
      name: "Business",
      description:
        "Built for brands and businesses that need more powerful tools.",
      price: "1,500",
      buttonText: "Choose Business",
      href: "/register",
      features: [
        "Everything in Pro",
        "Advanced analytics",
        "Custom domain",
        "Multiple ZeedBio pages",
        "Lead and contact collection",
        "Link thumbnails",
        "Priority support",
      ],
      popular: false,
    },
  ];

  return (
    <section
      id="pricing"
      className="bg-gray-50 dark:bg-gray-800"
    >
      <div className="mx-auto max-w-screen-xl px-4 py-12 lg:px-6 lg:py-24">
        
        {/* Heading */}
        <div className="mx-auto mb-16 max-w-screen-md text-center">
          <span className="mb-4 inline-block rounded-full bg-purple-100 px-4 py-1.5 text-sm font-semibold text-purple-700 dark:bg-purple-900/40 dark:text-purple-300">
            SIMPLE PRICING
          </span>

          <h2 className="mb-4 text-3xl font-extrabold tracking-tight text-gray-900 sm:text-4xl dark:text-white">
            Choose the perfect{" "}
            <span className="text-purple-600">plan for you</span>
          </h2>

          <p className="font-light leading-relaxed text-gray-500 sm:text-xl dark:text-gray-400">
            Start building your digital identity with ZeedBio. Upgrade anytime
            as your audience and business grow.
          </p>
        </div>

        {/* Pricing Cards */}
        <div className="mx-auto grid max-w-screen-lg grid-cols-1 gap-8 lg:grid-cols-3">
          {plans.map((plan) => (
            <div
              key={plan.name}
              className={`relative mx-auto flex w-full max-w-lg flex-col rounded-2xl bg-white p-6 text-center text-gray-900 transition duration-300 hover:-translate-y-1 ${
                plan.popular
                  ? "border-2 border-purple-600 shadow-xl dark:border-purple-500"
                  : "border border-gray-200 shadow-md dark:border-gray-700"
              } dark:bg-gray-900 dark:text-white xl:p-8`}
            >
              {/* Popular Badge */}
              {plan.popular && (
                <div className="absolute left-1/2 -top-4 -translate-x-1/2 whitespace-nowrap rounded-full bg-purple-600 px-4 py-1.5 text-sm font-semibold text-white shadow-lg">
                  Most Popular
                </div>
              )}

              {/* Plan Name */}
              <h3 className="mb-4 text-2xl font-bold">
                {plan.name}
              </h3>

              {/* Description */}
              <p className="min-h-[56px] font-light leading-relaxed text-gray-500 sm:text-lg dark:text-gray-400">
                {plan.description}
              </p>

              {/* Price */}
              <div className="my-8 flex items-baseline justify-center">
                <span className="mr-2 text-4xl font-extrabold sm:text-5xl">
                  {plan.price} DZD
                </span>

                <span className="text-gray-500 dark:text-gray-400">
                  /month
                </span>
              </div>

              {/* Features */}
              <ul className="mb-8 flex-1 space-y-4 text-left">
                {plan.features.map((feature, index) => (
                  <li
                    key={index}
                    className="flex items-center gap-3"
                  >
                    <CheckIcon highlighted={plan.popular} />

                    <span className="text-sm text-gray-700 sm:text-base dark:text-gray-300">
                      {feature}
                    </span>
                  </li>
                ))}
              </ul>

              {/* Button */}
              <a
                href={plan.href}
                className={`mt-auto rounded-xl px-5 py-3 text-center text-sm font-semibold transition focus:outline-none focus:ring-4 ${
                  plan.popular
                    ? "bg-purple-600 text-white hover:bg-purple-700 focus:ring-purple-300 dark:focus:ring-purple-900"
                    : "bg-gray-900 text-white hover:bg-purple-600 focus:ring-gray-300 dark:bg-white dark:text-gray-900 dark:hover:bg-purple-500 dark:hover:text-white"
                }`}
              >
                {plan.buttonText}
              </a>
            </div>
          ))}
        </div>

        {/* Annual Offer */}
        <div className="mx-auto mt-12 max-w-2xl rounded-2xl border border-purple-200 bg-purple-50 p-6 text-center dark:border-purple-900 dark:bg-gray-900">
          <div className="mb-2 text-3xl">🎉</div>

          <h3 className="mb-2 text-xl font-bold text-gray-900 dark:text-white">
            Save more with annual billing
          </h3>

          <p className="leading-relaxed text-gray-600 dark:text-gray-400">
            Pay yearly and get{" "}
            <strong className="font-bold text-purple-700 dark:text-purple-400">
              2 months free!
            </strong>{" "}
            Pro for{" "}
            <strong className="text-gray-900 dark:text-white">
              5,000 DZD/year
            </strong>{" "}
            or Business for{" "}
            <strong className="text-gray-900 dark:text-white">
              15,000 DZD/year
            </strong>
            .
          </p>
        </div>
      </div>
    </section>
  );
};

export default Pricing;